import { useEdgeResize } from "@/hooks/useEdgeResize";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home, SquarePen, HelpCircle, PanelLeft, LibraryBig, Palette, Settings } from "lucide-react";
import { AvatarMenu } from "./AvatarMenu";
import { ConversationsPanel } from "@/features/hector-agent-casediscovery-v3";
import { RedactionEnCoursBanner } from "@/features/redaction/components/RedactionEnCoursBanner";
import { SIDEBAR_COLLAPSED_WIDTH } from "@/hooks/useSidebarLayout";

/**
 * Largeur (px) de la bande de saisie du redimensionnement, centrée sur le bord gauche
 * de la fenêtre de page. Assez large pour se viser sans précision, assez étroite pour
 * ne mordre que de 5px sur la fenêtre — les listes de Dossiers et du dossier collent
 * leurs lignes à ce bord, et tout ce que la bande couvre cesse d'y être cliquable.
 */
const RESIZE_HANDLE_WIDTH = 10;

interface AppSidebarProps {
  onSignOut: () => void;
  /** Largeur courante (px) — pilotée par `useSidebarLayout`. */
  width: number;
  /** Replié en rail d'icônes (vs barre complète). */
  collapsed: boolean;
  /** Bascule replié / déplié. */
  onToggleCollapse: () => void;
  /** Nouvelle largeur pendant le drag (le parent borne min/max). */
  onWidthChange: (width: number) => void;
  /** Signale au parent le début/fin du drag (coupe la transition de marge). */
  onResizingChange: (resizing: boolean) => void;
}

const AppSidebar = ({
  onSignOut,
  width,
  collapsed,
  onToggleCollapse,
  onWidthChange,
  onResizingChange,
}: AppSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Le geste lui-même vit dans `useEdgeResize`, partagé avec le panneau d'aperçu
  // ancré à droite : deux bords opposés, un seul drag à maintenir.
  const { resizeHandleProps } = useEdgeResize({ side: "left", onWidthChange, onResizingChange });

  const navItems = [
    { href: "/", icon: Home, label: "Dossiers" },
    { href: "/modeles", icon: LibraryBig, label: "Bibliothèque de modèles" },
    { href: "/personnalisation", icon: Palette, label: "Personnalisation" },
  ];

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  // Un SEUL layout, adaptatif. Replié = rail étroit (SIDEBAR_COLLAPSED_WIDTH) : on
  // masque les libellés + la liste de conversations, on rétrécit, et les icônes se
  // CENTRENT dans le rail (`justify-center`) — en gardant EXACTEMENT les mêmes tailles
  // que déplié (nav h-4, Aide/Profil h-6). Centrer (plutôt que laisser à gauche) évite
  // que les grosses icônes paraissent décalées ; les centres restent alignés et le
  // déplacement repli↔dépli est minime et uniforme. Tooltips natifs (`title`) quand le
  // libellé est masqué. Largeur instantanée (pas de transition, sinon libellés qui
  // débordent / poignée clippée) ; le contenu glisse via sa transition de marge — fond
  // identique #fafafa → décalage bref invisible. pt-[7px] aligne le logo sur le chat.
  return (
    <aside
      style={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : width }}
      className="fixed left-0 top-0 h-screen bg-[#fafafa] flex flex-col z-40 hidden md:flex pt-[7px]"
    >
      {/* En-tête : logo (déplié) + bouton replier/déplier. En rail, le logo est masqué
          et le toggle est CENTRÉ dans le rail (comme les autres icônes). */}
      <div className={cn("flex items-center h-12", collapsed ? "justify-center" : "justify-between pl-[18px] pr-2")}>
        {!collapsed && (
          <Link to="/" className="flex items-center h-full">
            <img src="/logo_hector-black.png" alt="Hector" className="h-5 w-auto" />
          </Link>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Déplier la barre latérale" : "Réduire la barre latérale"}
          title={collapsed ? "Déplier" : undefined}
          className={cn(
            "flex flex-shrink-0 items-center justify-center text-grey-500 transition-colors hover:bg-[#F0F0EE] hover:text-charcoal",
            collapsed ? "h-9 w-9 rounded-lg" : "h-7 w-7 rounded-md",
          )}
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Navigation — « Nouvelle conversation » puis Dossiers. Libellés masqués en rail ;
          icônes centrées, mêmes tailles (h-4) qu'en déplié. */}
      <nav className="py-3 px-2">
        <button
          // An object target explicitly clears `?c=`. Passing only the pathname
          // left the browser URL blank while the persistent assistant view could
          // retain the prior route render, so its next Enter was still sent as A.
          onClick={() => navigate({ pathname: '/assistant', search: '' })}
          data-tour="new-conversation"
          title={collapsed ? "Nouvelle conversation" : undefined}
          className="w-full flex flex-row items-center py-2 rounded-lg transition-colors mb-1 text-charcoal hover:bg-[#F0F0EE]"
        >
          {/* Boîte d'icône de largeur fixe (w-10) → l'icône est centrée à 28px (= centre
              du rail) dans les DEUX états : aucun glissement horizontal au repli/dépli. */}
          <span className="flex w-10 flex-shrink-0 items-center justify-center">
            <SquarePen className="h-4 w-4" />
          </span>
          {!collapsed && <span className="text-[13px] font-normal leading-tight whitespace-nowrap">Nouvelle conversation</span>}
        </button>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              title={collapsed ? item.label : undefined}
              className={cn(
                "w-full flex flex-row items-center py-2 rounded-lg transition-colors mb-1",
                active
                  ? "bg-[#E5E5E3] text-charcoal"
                  : "text-charcoal hover:bg-[#F0F0EE]"
              )}
            >
              <span className="flex w-10 flex-shrink-0 items-center justify-center">
                <Icon className="h-4 w-4" />
              </span>
              {!collapsed && <span className="text-[13px] font-normal leading-tight whitespace-nowrap">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Rédaction en cours — AU-DESSUS de la zone qui défile, pour ne pas pouvoir
          s'échapper par le haut, et sous la nav pour rester adjacent à la ligne de
          conversation qui porte le même signal. Ne rend rien au repos : la liste ne
          lui cède de la hauteur que pendant qu'une rédaction tourne (#859). */}
      <RedactionEnCoursBanner collapsed={collapsed} />

      {/* Conversation list — masquée en rail ; le flex-1 garde le groupe du bas en place
          (Aide + Profil restent à la même latitude). */}
      <div className="flex-1 min-h-0 mt-4" data-tour={collapsed ? undefined : "conversations-list"}>
        {!collapsed && <ConversationsPanel />}
      </div>

      {/* Bottom group — Aide + profil. Libellé Aide masqué en rail ; icône (h-6) + avatar
          centrés, mêmes tailles (cf. AvatarMenu `collapsed` = centre + masque le texte). */}
      <div className="flex flex-col gap-0.5 px-2 pt-2 pb-3">
        <Link
          to="/aide"
          data-tour="help-link"
          title={collapsed ? "Aide" : undefined}
          className={cn(
            "w-full flex flex-row items-center py-1.5 rounded-lg transition-colors",
            isActive("/aide")
              ? "bg-[#E5E5E3] text-charcoal"
              : "text-charcoal hover:bg-[#F0F0EE]"
          )}
        >
          <span className="flex w-10 flex-shrink-0 items-center justify-center">
            <HelpCircle className="h-6 w-6" />
          </span>
          {!collapsed && <span className="text-[13px] font-normal leading-tight whitespace-nowrap">Aide</span>}
        </Link>
        <Link
          to="/settings"
          title={collapsed ? "Paramètres" : undefined}
          className={cn(
            "w-full flex flex-row items-center py-1.5 rounded-lg transition-colors",
            isActive("/settings") ? "bg-[#E5E5E3] text-charcoal" : "text-charcoal hover:bg-[#F0F0EE]"
          )}
        >
          <span className="flex w-10 flex-shrink-0 items-center justify-center">
            <Settings className="h-5 w-5" />
          </span>
          {!collapsed && <span className="text-[13px] font-normal leading-tight whitespace-nowrap">Paramètres</span>}
        </Link>
        <AvatarMenu onSignOut={onSignOut} collapsed={collapsed} />
      </div>

      {/* Poignée de redimensionnement (mode déplié uniquement) — bande INVISIBLE
          centrée sur le bord gauche de la fenêtre de page. Aucun trait : seul le
          curseur `col-resize` signale qu'on peut tirer.

          WHY centrée sur CE bord, et non sur le bord réel de la barre : la barre et la
          gouttière ont le même fond (#fafafa), donc le seul bord que l'avocat voit est
          le filet de la carte blanche, à `--content-gutter` du bord réel. Une bande
          calée sur le bord réel allume le curseur de redimensionnement dans la
          gouttière — à CÔTÉ de la fenêtre — et le laisse éteint sur le filet, c'est-à-
          dire précisément là où l'on vise (signalement produit 2026-09-10). La bande
          chevauche donc le filet à parts égales, comme le fait la poignée d'un split :
          `right` ramène son bord droit à `--content-gutter` + la moitié de sa largeur,
          au-delà du bord de la barre. */}
      {!collapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Redimensionner la barre latérale"
          {...resizeHandleProps}
          style={{
            right: `calc(-1 * (var(--content-gutter, 8px) + ${RESIZE_HANDLE_WIDTH / 2}px))`,
            width: RESIZE_HANDLE_WIDTH,
          }}
          className="absolute top-0 z-50 h-full cursor-col-resize"
        />
      )}
    </aside>
  );
};

export default AppSidebar;
